'use client';

/**
 * Framed phone "picture" for the tour's "What's inside / Your first steps"
 * slide (Veronika 2026-06-13: "make the picture smaller and really frame the
 * printscreen I sent"). It frames a REAL screenshot of the home screen inside
 * a small phone bezel — the most faithful preview possible. A pixel sheep
 * walks a lap around the frame's edge for a touch of life (same as the
 * marketing site). Decorative only; honors reduced-motion.
 *
 * Replace the screenshot by dropping a new file at public/tour/home-preview.jpg
 * and bumping the ?v= cache-buster below.
 */

// Pixel sheep that walks the perimeter of the frame.
const EdgeSheep = () => (
  <svg className="hm-edge-sheep" width={22} height={14} viewBox="0 0 32 20" shapeRendering="crispEdges" aria-hidden="true">
    <rect x="8" y="3" width="20" height="9" />
    <rect x="9" y="1" width="4" height="2" />
    <rect x="14" y="0" width="4" height="3" />
    <rect x="19" y="1" width="4" height="2" />
    <rect x="24" y="2" width="3" height="1" />
    <rect x="2" y="6" width="6" height="6" />
    <rect x="6" y="4" width="2" height="2" />
    <rect x="4" y="7" width="1" height="1" fill="var(--paper-1)" />
    <rect x="9" y="12" width="2" height="6" />
    <rect x="13" y="12" width="2" height="6" />
    <rect x="20" y="12" width="2" height="6" />
    <rect x="24" y="12" width="2" height="6" />
    <rect x="27" y="4" width="1" height="2" />
  </svg>
);

export function HomeMockup({ width = 158 }: { width?: number }) {
  // Tighter stage padding when the frame is small so it doesn't waste vertical
  // space (the install reminder shrinks the frame to fit a phone — 2026-06-18).
  const pad = width < 120 ? 8 : 14;
  return (
    <div className="hm-stage" aria-hidden="true" style={{ padding: pad }}>
      <div className="hm-phone" style={{ width }}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img className="hm-shot" src="/tour/home-preview.jpg?v=1" alt="" />
      </div>
      <EdgeSheep />

      <style jsx>{`
        .hm-stage {
          position: relative;
          display: inline-block;
          padding: 14px;
        }
        .hm-phone {
          width: 158px;
          max-width: 100%;
          border: 6px solid var(--ink);
          border-radius: 26px;
          overflow: hidden;
          background: var(--paper-1);
          line-height: 0;
          box-shadow: 0 10px 30px rgba(15, 14, 12, 0.2);
        }
        .hm-shot {
          display: block;
          width: 100%;
          height: auto;
        }
        /* Sheep walks a counter-clockwise lap around the frame's edge. */
        :global(.hm-edge-sheep) {
          position: absolute;
          top: 0;
          left: 0;
          fill: var(--ink);
          animation: hm-edge-walk 20s linear infinite;
        }
        @keyframes hm-edge-walk {
          0% {
            top: 0%;
            left: 100%;
            transform: translate(-50%, -50%) rotate(0deg);
          }
          25% {
            top: 0%;
            left: 0%;
            transform: translate(-50%, -50%) rotate(0deg);
          }
          25.01% {
            top: 0%;
            left: 0%;
            transform: translate(-50%, -50%) rotate(-90deg);
          }
          50% {
            top: 100%;
            left: 0%;
            transform: translate(-50%, -50%) rotate(-90deg);
          }
          50.01% {
            top: 100%;
            left: 0%;
            transform: translate(-50%, -50%) rotate(180deg);
          }
          75% {
            top: 100%;
            left: 100%;
            transform: translate(-50%, -50%) rotate(180deg);
          }
          75.01% {
            top: 100%;
            left: 100%;
            transform: translate(-50%, -50%) rotate(90deg);
          }
          100% {
            top: 0%;
            left: 100%;
            transform: translate(-50%, -50%) rotate(90deg);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          :global(.hm-edge-sheep) {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
